import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export function resolvePodPhoto(url) {
  if (!url) return "";
  if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

/**
 * Homepage section showing the nearest pods to the visitor.
 * - Auto-requests browser geolocation on mount to sort nearest-first.
 * - Falls back gracefully (no distance shown) if permission denied.
 */
export default function PodNetworkSection() {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoState, setGeoState] = useState("prompting"); // prompting | granted | denied | unavailable

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGeoState("unavailable");
      fetchPods();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState("granted");
        fetchPods(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        setGeoState("denied");
        fetchPods();
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  }, []);

  const fetchPods = async (lat, lng) => {
    setLoading(true);
    try {
      const params = lat != null && lng != null ? `?lat=${lat}&lng=${lng}` : "";
      const { data } = await api.get(`/pods${params}`);
      setPods(data.items || []);
    } catch { setPods([]); }
    finally { setLoading(false); }
  };

  return (
    <section id="pods" className="py-20 bg-slate-50 border-b border-slate-100" data-testid="pods-network-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">
            The Pod Network
          </p>
          <h2 className="text-3xl md:text-5xl font-black mb-4 uppercase tracking-tightest text-brand-blue">
            Find Your Nearest Pod
          </h2>
          <p className="text-slate-600 font-bold text-sm md:text-base max-w-2xl mx-auto">
            {geoState === "granted"
              ? "Sorted by distance from your current location."
              : geoState === "denied"
              ? "Enable location to see distance from you — otherwise, here's the full network."
              : geoState === "prompting"
              ? "Detecting your location to show nearest pods…"
              : "Our growing network of Denver pods."}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500 font-black uppercase text-xs tracking-widest" data-testid="pods-loading">
            <i className="fas fa-spinner fa-spin mr-2" /> Loading pods…
          </div>
        ) : pods.length === 0 ? (
          <div className="text-center py-16 text-slate-500 font-bold text-sm" data-testid="pods-empty">
            No pods found. Please try again later.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pods-grid">
            {pods.map((p) => <PodCard key={p.id} pod={p} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function PodCard({ pod }) {
  const isActive = pod.status === "active";
  return (
    <Link
      to={`/pods/${pod.slug}`}
      className="group block bg-white rounded-[28px] overflow-hidden border border-slate-100 hover:border-brand-gold hover:shadow-xl transition-all"
      data-testid={`pod-card-${pod.slug}`}
    >
      {/* Photo */}
      <div className="relative h-52 overflow-hidden bg-slate-200">
        <img
          src={resolvePodPhoto(pod.photo_url)}
          alt={pod.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        {/* Status badge */}
        <div className="absolute top-4 left-4">
          {isActive ? (
            <span className="bg-emerald-500 text-white text-[9px] font-black uppercase tracking-[0.3em] px-3 py-1.5 rounded-full shadow-md">
              <i className="fas fa-circle text-[6px] mr-1.5 animate-pulse" /> Live Now
            </span>
          ) : (
            <span className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-[0.3em] px-3 py-1.5 rounded-full shadow-md">
              <i className="fas fa-hourglass-half mr-1.5" /> Opening Soon
            </span>
          )}
        </div>
        {/* Distance badge */}
        {pod.distance_miles != null && (
          <div className="absolute top-4 right-4 bg-white/95 backdrop-blur text-brand-blue text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full shadow-md" data-testid={`pod-distance-${pod.slug}`}>
            <i className="fas fa-location-arrow mr-1.5" />
            {pod.distance_miles} mi
          </div>
        )}
        {/* Neighborhood name — bottom overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-1">
            {pod.neighborhood}
          </p>
          <h3 className="text-2xl font-black uppercase tracking-tightest text-white leading-none">
            {pod.name}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <p className="text-slate-600 text-sm font-bold mb-4 leading-relaxed line-clamp-2">
          {pod.tagline || "Self-serve party rentals in your neighborhood."}
        </p>
        <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
          <span className="text-slate-400">
            <i className="fas fa-clock mr-1.5" /> {pod.hours || "24/7"}
          </span>
          <span className={`${isActive ? "text-brand-red" : "text-brand-blue"} group-hover:translate-x-1 transition-transform`}>
            {isActive ? "Rent Now" : "Notify Me"} <i className="fas fa-arrow-right ml-1.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
